import { ConnectProto } from "@racoon/api";

import { TRUCK_TYPE_ORDER, isTruckArchived, truckTypeOf } from "~/shared/lib/truck-type";
import type { BucketedJob } from "./use-pickup-status";
import { isSetId, jobVolume } from "./pickup-status.lib";

export type FleetTypeLine = {
  type: ConnectProto.TruckType;
  onLine: number;
  total: number;
  volume: number;
  weightT: number;
};

export type FleetOnLine = {
  types: FleetTypeLine[];
  onLine: number;
  total: number;
};

type Shift = Pick<ConnectProto.DriverOnShift, "id" | "truckId">;
type ShiftRef = { driverOnShiftId?: bigint | string; driverOnShift?: Shift };

/** Collected volume and weighed tonnes per truck type, and trucks with a shift on the day.
 *  `dayShifts` are the day's shifts; `allShifts` also resolves work from an overnight shift. */
export function summarizeFleetOnLine({
  trucks,
  dayShifts,
  allShifts,
  jobs,
  routes,
}: {
  trucks: ConnectProto.Truck[];
  dayShifts: Shift[];
  allShifts: Shift[];
  jobs: BucketedJob[];
  routes: ConnectProto.Route[];
}): FleetOnLine {
  // Work done by a since-archived truck still counts for its type; the truck itself is no
  // longer part of the fleet («N из M»).
  const typeByTruck = new Map<string, ConnectProto.TruckType>();
  const activeTrucks = new Set<string>();
  const totals = new Map<ConnectProto.TruckType, number>();
  for (const truck of trucks) {
    const type = truckTypeOf(truck);
    typeByTruck.set(String(truck.id), type);
    if (isTruckArchived(truck)) continue;
    activeTrucks.add(String(truck.id));
    totals.set(type, (totals.get(type) ?? 0) + 1);
  }

  const onLineTrucks = new Map<ConnectProto.TruckType, Set<string>>();
  for (const shift of dayShifts) {
    const type = typeByTruck.get(String(shift.truckId));
    if (type === undefined || !activeTrucks.has(String(shift.truckId))) continue;
    if (!onLineTrucks.has(type)) onLineTrucks.set(type, new Set());
    onLineTrucks.get(type)!.add(String(shift.truckId));
  }

  const truckByShift = new Map(allShifts.map((s) => [String(s.id), String(s.truckId)]));
  const typeOf = (ref: ShiftRef) => {
    const embedded = ref.driverOnShift?.truckId;
    const truckId = isSetId(embedded) ? embedded : truckByShift.get(String(ref.driverOnShiftId));
    return truckId === undefined ? undefined : typeByTruck.get(String(truckId));
  };
  const routeById = new Map(routes.map((r) => [String(r.id), r]));

  const volume = new Map<ConnectProto.TruckType, number>();
  const doneRouteIds = new Set<string>();
  for (const { job, bucket } of jobs) {
    if (bucket !== "done") continue;
    const route = isSetId(job.routeId) ? routeById.get(String(job.routeId)) : undefined;
    if (route) doneRouteIds.add(String(route.id));
    // The route's truck stands in only for a job with no shift of its own.
    const type = isSetId(job.driverOnShiftId) ? typeOf(job) : route && typeOf(route);
    if (type !== undefined) volume.set(type, (volume.get(type) ?? 0) + jobVolume(job, bucket));
  }

  // Weighbridge net is per trip, so a route counts once however many jobs it served.
  const weightKg = new Map<ConnectProto.TruckType, number>();
  for (const id of doneRouteIds) {
    const route = routeById.get(id)!;
    const type = typeOf(route);
    const kg = route.scaleRecord?.nettoWeightKg ?? 0;
    if (type !== undefined) weightKg.set(type, (weightKg.get(type) ?? 0) + kg);
  }

  const types = TRUCK_TYPE_ORDER.filter(
    (type) => type !== ConnectProto.TruckType.UNDEFINED && (totals.get(type) ?? 0) > 0,
  ).map((type) => ({
    type,
    onLine: onLineTrucks.get(type)?.size ?? 0,
    total: totals.get(type)!,
    volume: volume.get(type) ?? 0,
    weightT: (weightKg.get(type) ?? 0) / 1000,
  }));

  return {
    types,
    onLine: types.reduce((sum, t) => sum + t.onLine, 0),
    total: types.reduce((sum, t) => sum + t.total, 0),
  };
}

export type ShareTone = "good" | "fair" | "low" | "poor";

/** Judged on the shown, one-decimal value, so «80%» is never green. */
export function shareTone(pct: number): ShareTone {
  pct = Math.round(pct * 10) / 10;
  if (pct > 80) return "good";
  if (pct > 60) return "fair";
  if (pct > 40) return "low";
  return "poor";
}
